import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { ArrowLeft, Printer } from 'lucide-react';

const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);

const formatReceiptDate = (dateStr) => {
  if (!dateStr) return '--';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    const day = d.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'ST' : day === 2 || day === 22 ? 'ND' : day === 3 || day === 23 ? 'RD' : 'TH';
    const months = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    return `${day}${suffix} OF ${months[d.getMonth()]}, ${d.getFullYear()}`;
  } catch { return dateStr; }
};

const numberToWords = (num) => {
  // Inline approximate words; matches the BOReceipt style for consistency
  if (!num || isNaN(num)) return 'Zero Naira Only';
  return `${formatNaira(num)} Only`;
};

const BODevelopmentFeeReceipt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fee, setFee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    boAPI.getDevelopmentFee(id)
      .then(r => setFee(r.data))
      .catch(() => { toast.error('Failed to load receipt'); navigate(-1); })
      .finally(() => setLoading(false));
  }, [id]);

  const handlePrint = () => {
    const orig = document.title;
    document.title = `Medichi_DevFee_Receipt_${(fee?.client_info?.full_name || 'Client').replace(/\s+/g, '_')}`;
    window.print();
    setTimeout(() => { document.title = orig; }, 1000);
  };

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0052A2]" /></div></BOLayout>;
  if (!fee) return null;

  const client = fee.client_info || {};
  const plots = fee.plots_info || [];
  const plotDesc = plots.length
    ? `${plots.length} plot${plots.length > 1 ? 's' : ''} (${plots.map(p => p.plot_number).join(', ')}) at ${plots[0].estate_name}${plots[0].location_block ? `, ${plots[0].location_block}` : ''}${plots[0].estate_location ? `, ${plots[0].estate_location}` : ''}`
    : '--';

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-dev-fee-receipt-page">
        <div className="flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-neutral-100"><ArrowLeft className="h-5 w-5" /></button>
            <h1 className="text-2xl font-serif font-bold text-neutral-900">Development Fee Receipt</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={handlePrint} variant="outline" className="rounded-xl" data-testid="print-dev-receipt-btn">
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
          </div>
        </div>

        <div className="max-w-2xl mx-auto print:max-w-none" id="receipt-content">
          <div className="bg-white border border-neutral-200 rounded-2xl shadow-lg overflow-hidden print:shadow-none print:border-0">
            <div className="bg-[#0052A2] text-white p-6 text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <div className="flex items-center justify-center gap-3 mb-1">
                <svg width="40" height="40" viewBox="0 0 120 120" fill="none">
                  <circle cx="60" cy="60" r="55" stroke="white" strokeWidth="5" fill="none" />
                  <path d="M38 95V40L60 20L82 40V95" stroke="white" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <path d="M38 40L60 58L82 40" stroke="white" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <line x1="60" y1="58" x2="60" y2="95" stroke="white" strokeWidth="5" strokeLinecap="round" />
                </svg>
                <h2 className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>MEDICHI HOMES AND SHELTER</h2>
              </div>
              <p className="text-blue-100 text-xs">Suite B11 Apedic Plaza, Opposite Kilimanjaro By Stadium Nnebisi Road, Asaba Delta State</p>
              <p className="text-blue-200 text-xs mt-1">Tel: 09061621162, 07073180266 | Email: medichihomes@gmail.com</p>
            </div>

            <div className="px-6 py-3 bg-blue-50 border-b text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <p className="text-[#0052A2] text-xs font-semibold tracking-widest uppercase">Development Fee Receipt</p>
            </div>

            <div className="px-6 py-4 bg-neutral-50 border-b flex justify-between items-center">
              <div>
                <p className="text-xs text-neutral-500">Receipt No.</p>
                <p className="font-mono font-bold text-lg" data-testid="dev-receipt-number">{fee.receipt_number}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-neutral-500">Date</p>
                <p className="font-semibold">{formatReceiptDate(fee.fee_date)}</p>
              </div>
            </div>

            <div className="px-6 py-4 border-b">
              <p className="text-xs text-neutral-500 mb-1">RECEIVED FROM</p>
              <p className="font-bold text-lg uppercase">{client.full_name || '--'}</p>
              {client.phone && <p className="text-sm text-neutral-600">{client.phone}</p>}
              {client.email && <p className="text-sm text-neutral-600">{client.email}</p>}
            </div>

            <div className="px-6 py-5 border-b">
              <p className="text-xs text-neutral-500 mb-1">THE SUM OF</p>
              <p className="font-semibold text-base">{numberToWords(fee.amount)}</p>
              <p className="text-xs text-neutral-500 mt-3 mb-1">BEING DEVELOPMENT FEE FOR</p>
              <p className="font-medium">{plotDesc}</p>
              {fee.purpose && <p className="text-sm text-neutral-700 mt-2"><strong>Purpose:</strong> {fee.purpose}</p>}
            </div>

            <div className="px-6 py-5 border-b">
              <div className="flex justify-between items-baseline">
                <p className="text-sm text-neutral-500">AMOUNT PAID</p>
                <p className="text-3xl font-bold text-[#0052A2]" data-testid="dev-receipt-amount">{formatNaira(fee.amount)}</p>
              </div>
              <p className="text-xs text-neutral-500 mt-1">via {fee.payment_method?.replace('_', ' ') || 'bank transfer'}{fee.bank_reference ? ` · Ref: ${fee.bank_reference}` : ''}</p>
            </div>

            <div className="px-6 py-6 flex justify-between items-end">
              <div>
                <p className="text-xs text-neutral-500 mb-8">RECORDED BY</p>
                <div className="border-t border-neutral-300 pt-1 w-44">
                  <p className="text-sm font-medium">{fee.recorded_by_name || '--'}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-neutral-500 mb-1">AUTHORIZED SIGNATORY</p>
                <p className="text-2xl text-[#0052A2] mt-2" style={{ fontFamily: 'cursive' }}>Ekene Medichi</p>
                <div className="border-t border-neutral-300 pt-1 w-44 ml-auto">
                  <p className="text-xs text-neutral-400">Ekene Medichi</p>
                </div>
              </div>
            </div>

            <div className="bg-neutral-50 px-6 py-3 text-center" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
              <p className="text-[10px] text-neutral-500">© Medichi Homes and Shelter. Thank you for your investment in our community.</p>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          body * { visibility: hidden; }
          #receipt-content, #receipt-content * { visibility: visible; }
          #receipt-content {
            position: absolute; left: 0; top: 0; width: 100%;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          [data-testid="bo-layout"] > aside { display: none !important; }
          [data-testid="bo-layout"] > main > div:first-child { display: none !important; }
        }
      `}</style>
    </BOLayout>
  );
};

export default BODevelopmentFeeReceipt;
